import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import PatientCard from "../components/PatientCard";
import PatientPrescriptions from "../components/PatientPrescriptions";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import "../styles/PatientContainer.css";
import { SERVER_URL } from "../config";
import ProviderNotesCard, {
  ProviderNote,
} from "../components/ProviderNotesCard";
import MessagesModal from "../components/modals/MessagesModal";
import PatientInvoice from "../components/PatientInvoices";
import PatientInvoices from "../components/PatientInvoices";
import ProviderNotesContainer from "../components/ProviderNotesContainer";

interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  region: string;
  city: string;
  dob: string;
  phone: string;
  idFront?: string;
  selfie?: string;
  idBack?: string;
  isIDVerified?: boolean;
  providerNotes: ProviderNote[];
  userId: string;
  sentMessages: string;
}

const PatientContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for the active tab
  const [activeTab, setActiveTab] = useState<
    "providerNotes" | "patientPrescriptions" | "patientInvoices" | "messages"
  >("patientPrescriptions");

  const fetchPatientData = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${SERVER_URL}/api/patient/patients/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Error fetching patient: ${response.statusText}`);
      }
      const data = await response.json();
      const formattedPatient: Patient = {
        id: data.id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.user.email,
        address: data.address ?? "N/A",
        region: data.region ?? "N/A",
        city: data.city ?? "N/A",
        dob: data.dob ?? "N/A",
        phone: data.phone || "N/A",
        idFront: data.idFront,
        selfie: data.selfie,
        idBack: data.idBack,
        isIDVerified: data.isIDVerified,
        providerNotes: data.providerNotes,
        userId: data.user.id,
        sentMessages: data.user.sentMessages,
      };
      setPatient(formattedPatient);
    } catch (err) {
      console.error("Error fetching patient data:", err);
      setError("Failed to fetch patient data.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPatientData();
  }, [fetchPatientData]);

  if (loading) {
    return <p>Loading patient data...</p>;
  }
  if (error) {
    return <p style={{ color: "red" }}>{error}</p>;
  }
  if (!patient) {
    return (
      <div className="patient-container">
        <h2 className="patient-header">Patient not found.</h2>
      </div>
    );
  }

  const handleNoteSave = (newNote: string) => {
    console.log("New provider note saved:", newNote);
    // Additional logic can be added here if needed.
  };

  // Switch-case to render content based on active tab.
  const renderContent = () => {
    switch (activeTab) {
      case "providerNotes":
        return (
          <ProviderNotesContainer
            initialNotes={patient.providerNotes || []}
            patientProfileId={patient.id || ""}
            onSave={handleNoteSave}
          />
        );
      case "patientPrescriptions":
        return <PatientPrescriptions patientId={patient.userId} />;
      case "patientInvoices":
        return <PatientInvoices patientId={patient.id} />;
      case "messages":
        // Render the MessagesModal directly. When onClose is triggered, change activeTab.
        return (
          <MessagesModal
            patientProfile={patient}
            onClose={() => setActiveTab("patientPrescriptions")}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="patient-container">
      <div className="patient-section">
        <PatientCard patient={patient} />
      </div>

      {/* Navigation Bar for action buttons */}
      <div
        className="patient-nav"
        style={{ margin: "1rem 0", textAlign: "left" }}
      >
        <button onClick={() => setActiveTab("providerNotes")}>
          Provider Notes
        </button>
        <button onClick={() => setActiveTab("patientPrescriptions")}>
          Patient Prescriptions
        </button>
        <button onClick={() => setActiveTab("patientInvoices")}>
          Patient Invoices
        </button>
        <button onClick={() => setActiveTab("messages")}>Messages</button>
      </div>

      {/* Render the content for the active tab */}
      <div className="patient-content">{renderContent()}</div>
    </div>
  );
};

export default PatientContainer;
